import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import EventDispatcher from "./components/EventDispatcher";
import InvitePanel from "./components/InvitePanel";
import HubGuideEditor from "./components/HubGuideEditor";
import StudentProgressDashboard from "./components/StudentProgressDashboard";
import WorkspaceStatsDashboard from "./components/WorkspaceStatsDashboard";
import AnnouncementComposer from "./components/AnnouncementComposer";
import WeeklyReportManager from "./components/WeeklyReportManager";
import ApprovalManagerPanel from "./components/ApprovalManagerPanel";
import KPIManagerPanel from "./components/KPIManagerPanel";
import FinalPresentationManager from "./components/FinalPresentationManager";

type Tab = "students" | "events" | "invite" | "guide" | "analytics" | "announce" | "reports" | "approval" | "kpi" | "presentation";

type WorkspaceRow = {
  id: string;
  name: string;
  invite_code: string;
  play_mode: string;
  is_active: boolean;
  created_at: string;
  _memberCount?: number;
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("students");
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string>("");
  const [loadingWs, setLoadingWs] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [newWsMode, setNewWsMode] = useState<'A' | 'B'>('B');
  const [creating, setCreating] = useState(false);

  // 관리자 권한 가드 — 학생 계정은 즉시 대시보드로 리디렉션
  useEffect(() => {
    if (!profile) return;
    if (profile.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [profile, navigate]);

  const fetchWorkspaces = async () => {
    if (!user) return;
    setLoadingWs(true);
    const { data } = await supabase
      .from('workspaces')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });

    if (data && data.length > 0) {
      setWorkspaces(data as WorkspaceRow[]);
      if (!activeWorkspaceId) setActiveWorkspaceId(data[0].id);
    }
    setLoadingWs(false);
  };

  useEffect(() => { fetchWorkspaces(); }, [user]);

  const handleCreateWorkspace = async () => {
    if (!user || !newWsName.trim()) return;
    setCreating(true);
    // 6자리 영문+숫자 초대 코드 자동 생성
    const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    const { data, error } = await supabase.from('workspaces').insert({
      name: newWsName.trim(),
      owner_id: user.id,
      play_mode: newWsMode,
      invite_code: inviteCode,
    }).select().maybeSingle();

    if (!error && data) {
      setWorkspaces(prev => [data as WorkspaceRow, ...prev]);
      setActiveWorkspaceId(data.id);
      setShowCreateModal(false);
      setNewWsName('');
    }
    setCreating(false);
  };

  const activeWorkspace = workspaces.find(w => w.id === activeWorkspaceId);

  // 권한 없는 경우 빈 화면 (useEffect에서 리디렉션 처리)
  if (profile && profile.role !== 'admin') return null;

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "students", label: "학생 현황 대시보드", icon: "ri-group-line" },
    { id: "analytics", label: "통계 분석", icon: "ri-bar-chart-2-line" },
    { id: "reports", label: "주간 업무 보고서", icon: "ri-file-text-line" },
    { id: "approval", label: "전자결재 관리", icon: "ri-file-paper-2-line" },
    { id: "kpi", label: "성과 KPI 관리", icon: "ri-target-line" },
    { id: "presentation", label: "최종 발표 평가", icon: "ri-presentation-line" },
    { id: "events", label: "이벤트 발생기", icon: "ri-remote-control-line" },
    { id: "announce", label: "공지사항 발송", icon: "ri-megaphone-line" },
    { id: "invite", label: "초대 & 권한 관리", icon: "ri-user-add-line" },
    { id: "guide", label: "허브 가이드 편집", icon: "ri-edit-box-line" },
  ];

  return (
    <div className="min-h-screen bg-[#0D0F1A] flex flex-col">
      {/* Admin TopBar */}
      <header className="bg-[#111318] border-b border-[#252830] px-6 py-4 flex items-center gap-4 sticky top-0 z-40">
        <button type="button" onClick={() => navigate("/mode-select")} className="w-8 h-8 flex items-center justify-center text-[#5A5E72] hover:text-white transition-colors cursor-pointer">
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <div className="flex items-center gap-3">
          <img src="https://storage.readdy-site.link/project_files/e81f0f74-7f28-46c8-8485-3df778381a50/9a17726c-ea2b-4ef1-b154-44e1a8dd8821_Gemini_Generated_Image_at6qf4at6qf4at6q-removebg-preview.png?v=7e9fc5a9989fd2f2c3e90b758ae037ba" alt="K-ArtPEN" className="w-8 h-8 object-contain rounded-lg" />
          <div>
            <div className="text-[#111111] font-bold text-sm leading-tight">K-ArtPEN</div>
            <div className="text-[#E8375F] text-[10px] tracking-widest font-semibold">ADMIN CONSOLE</div>
          </div>
        </div>

        <div className="ml-4 flex-1 flex items-center gap-2">
          <span className="text-[#5A5E72] text-xs">워크스페이스:</span>
          {loadingWs ? (
            <span className="text-[#5A5E72] text-xs animate-pulse">로딩 중...</span>
          ) : workspaces.length === 0 ? (
            <span className="text-[#FF9F43] text-xs">워크스페이스 없음</span>
          ) : (
            <select
              value={activeWorkspaceId}
              onChange={(e) => setActiveWorkspaceId(e.target.value)}
              className="bg-[#1C1F2E] text-white text-xs px-3 py-1.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] cursor-pointer"
            >
              {workspaces.map((ws) => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E8375F]/15 border border-[#E8375F]/30 text-[#E8375F] text-xs rounded-lg hover:bg-[#E8375F]/25 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>새 워크스페이스
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#E8375F] to-[#FF9F43] text-white text-xs font-bold">
            {profile?.display_name?.[0] ?? '관'}
          </div>
          <div>
            <div className="text-[#111111] text-xs font-medium">{profile?.display_name ?? '강사 계정'}</div>
            <div className="text-[#5A5E72] text-[10px]">Administrator</div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Left Sidebar - Tabs */}
        <aside className="w-52 bg-[#111318] border-r border-[#252830] p-3 flex flex-col gap-1 pt-5">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? "bg-[#E8375F]/15 text-[#E8375F] font-semibold" : "text-[#8B91A8] hover:text-white hover:bg-[#1C1F2E]"
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${tab.icon} text-base`}></i>
              </div>
              <span className="leading-tight">{tab.label}</span>
            </button>
          ))}

          {/* Workspace Info */}
          {activeWorkspace && (
            <div className="mt-auto pt-4 border-t border-[#252830] mt-8">
              <div className="px-2 space-y-1.5">
                <div className="text-[#3E4155] text-[10px] uppercase tracking-widest mb-2">워크스페이스 정보</div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#5A5E72]">모드</span>
                  <span className="text-[#333333]">Mode {activeWorkspace.play_mode}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#5A5E72]">초대코드</span>
                  <span className="text-[#E8375F] font-mono text-[10px]">{activeWorkspace.invite_code}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#5A5E72]">생성일</span>
                  <span className="text-white text-[10px]">{new Date(activeWorkspace.created_at).toLocaleDateString('ko-KR')}</span>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="mb-5">
            <h2 className="text-[#111111] font-bold text-lg">{tabs.find(t => t.id === activeTab)?.label}</h2>
            <p className="text-[#5A5E72] text-sm mt-1">
              {activeTab === "students" && "초대 코드로 입장한 전체 학생의 직급·자본금·태스크 완료 현황을 한눈에 확인합니다."}
              {activeTab === "analytics" && "워크스페이스 전체 학생의 자본금·직급 분포, 허브별 태스크 완료 현황을 시각화합니다."}
              {activeTab === "reports" && "학생이 제출한 주간 업무 보고서를 확인하고 피드백(완료/반려)을 남깁니다."}
              {activeTab === "approval" && "학생이 제출한 전자결재 문서를 검토하고 승인 또는 반려 처리합니다."}
              {activeTab === "kpi" && "전체 학생의 KPI 목표 설정 현황과 달성률을 확인합니다."}
              {activeTab === "presentation" && "학생이 제출한 최종 발표 자료를 평가하고 팀 공동 KPI 목표를 설정합니다."}
              {activeTab === "events" && "특정 팀에 이벤트를 수동으로 발송하여 난이도와 시나리오를 조율합니다."}
              {activeTab === "announce" && "전체 학생의 인박스로 공지사항을 발송하고 발송 이력을 관리합니다."}
              {activeTab === "invite" && "학생들을 워크스페이스에 초대하고 팀과 역할을 배정합니다."}
              {activeTab === "guide" && "각 직무 허브의 할 일 가이드 내용을 직접 편집하고 커스터마이징합니다."}
            </p>
          </div>

          {activeTab === "students" && activeWorkspaceId && <StudentProgressDashboard workspaceId={activeWorkspaceId} />}
          {activeTab === "analytics" && activeWorkspaceId && <WorkspaceStatsDashboard workspaceId={activeWorkspaceId} />}
          {activeTab === "reports" && activeWorkspaceId && <WeeklyReportManager workspaceId={activeWorkspaceId} />}
          {activeTab === "approval" && activeWorkspaceId && <ApprovalManagerPanel workspaceId={activeWorkspaceId} />}
          {activeTab === "approval" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-file-paper-2-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "kpi" && activeWorkspaceId && <KPIManagerPanel workspaceId={activeWorkspaceId} />}
          {activeTab === "kpi" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-target-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "presentation" && activeWorkspaceId && <FinalPresentationManager workspaceId={activeWorkspaceId} />}
          {activeTab === "presentation" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-presentation-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "reports" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-file-text-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "analytics" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-bar-chart-2-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "events" && <EventDispatcher />}
          {activeTab === "announce" && activeWorkspaceId && <AnnouncementComposer workspaceId={activeWorkspaceId} />}
          {activeTab === "announce" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-megaphone-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
          {activeTab === "invite" && <InvitePanel workspaceId={activeWorkspaceId} inviteCode={activeWorkspace?.invite_code ?? ''} />}
          {activeTab === "guide" && activeWorkspaceId && <HubGuideEditor workspaceId={activeWorkspaceId} />}
          {activeTab === "guide" && !activeWorkspaceId && (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-building-2-line text-3xl text-[#2A2A2A] mb-3"></i>
              <p className="text-[#5A5E72] text-sm">워크스페이스를 먼저 선택하세요</p>
            </div>
          )}
        </main>
      </div>

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6">
          <div className="bg-[#1C1F2E] rounded-2xl border border-[#2A2D3E] p-6 w-full max-w-md">
            <h3 className="text-[#111111] font-bold text-base mb-5">새 워크스페이스 만들기</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[#8B91A8] text-xs mb-1.5 block">워크스페이스 이름</label>
                <input
                  type="text"
                  value={newWsName}
                  onChange={e => setNewWsName(e.target.value)}
                  placeholder="예: 2026년 1학기 문화예술창업반"
                  className="w-full bg-[#252830] text-white text-sm px-3 py-2.5 rounded-lg border border-[#2A2D3E] focus:outline-none focus:border-[#E8375F] placeholder-[#3E4155]"
                />
              </div>
              <div>
                <label className="text-[#8B91A8] text-xs mb-1.5 block">플레이 모드</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['A', 'B'] as const).map(m => (
                    <button key={m} type="button" onClick={() => setNewWsMode(m)}
                      className={`py-2.5 rounded-lg text-sm font-medium border transition-all whitespace-nowrap cursor-pointer ${newWsMode === m ? 'border-[#E8375F] bg-[#E8375F]/10 text-[#E8375F]' : 'border-[#2A2D3E] text-[#8B91A8]'}`}>
                      {m === 'A' ? 'Mode A (창업)' : 'Mode B (실무)'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 py-2.5 rounded-lg border border-[#2A2D3E] text-[#8B91A8] text-sm hover:text-white transition-colors cursor-pointer whitespace-nowrap">취소</button>
                <button type="button" onClick={handleCreateWorkspace} disabled={creating || !newWsName.trim()}
                  className="flex-1 py-2.5 rounded-lg bg-[#E8375F] text-white text-sm font-semibold hover:bg-[#FF4B7A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {creating ? '생성 중...' : '생성하기'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
